/* Copyright (c) 2019 - 2024. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState } from "react";
import { Button, Segment, Portal, Header, Table } from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { getApplications } from "./apsApi";
import ConfirmationDialog from "../../ConfirmationDialog";
import { Ico } from "../../Icon/Icon";

interface ApplicationsProps {
  org: any;
  onCloseDialog: any;
}

const Applications: React.FC<ApplicationsProps> = ({ org, onCloseDialog }) => {
  const [apps, setApps] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<MessageDialogParams | null>(null);

  const getApplicationsForOrg = async () => {
    setLoading(true);
    const appsList = await getApplications(org.id, "ALL");
    setApps(appsList);
    setLoading(false);
  };

  const onMount = () => {
    getApplicationsForOrg();
  };

  const subscriptionType = (app: any): string => {
    switch (app.subscriptionType) {
      case "XTD_PLATFORM":
        return "XTD";
      case "COUNTERSPY_PLATFORM":
        return "Counterspy";
      case "APPSHIELD_PLATFORM":
      case "APPSHIELD_STANDALONE":
        return "App Shield";
      default:
        return app.subscriptionType ? app.subscriptionType : "Unknown";
    }
  };

  const appRow = (app: any) => {
    return (
      <Table.Row key={app.id}>
        <Table.Cell>
          {(app.iconUrl && (
            <img
              src={app.iconUrl}
              alt="App icon"
              style={{
                width: "24px",
                height: "24px",
                margin: "0 5px 0 0",
                maxWidth: "none",
                verticalAlign: "middle",
              }}
            />
          )) || (
            <Ico
              name={app.os === "android" ? "android" : "ios"}
              style={{
                margin: "0 5px 0 0",
                width: "24px",
                filter: "contrast(0.5)",
                maxWidth: "none",
              }}
            />
          )}
          {app.applicationName}
        </Table.Cell>
        <Table.Cell>{app.applicationPackageId}</Table.Cell>
        <Table.Cell>{app.os === "android" ? "Android" : "iOS"}</Table.Cell>
        <Table.Cell>{subscriptionType(app)}</Table.Cell>
      </Table.Row>
    );
  };

  return (
    <Portal
      open={true}
      onMount={onMount}
      closeOnEscape
      closeOnDocumentClick
      onClose={() => {
        !message && onCloseDialog(null);
      }}
    >
      <Segment loading={loading} id={styles.appsSegment}>
        <Header>Applications for {org.name}</Header>
        {apps && (
          <div style={{ overflowX: "auto" }}>
            <Table celled className={styles.usersTable}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Package ID</Table.HeaderCell>
                  <Table.HeaderCell>OS</Table.HeaderCell>
                  <Table.HeaderCell>Subscription</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{apps.map((app: any) => appRow(app))}</Table.Body>
            </Table>
          </div>
        )}

        {message && (
          <ConfirmationDialog
            title={message.title}
            message={message.message}
            onOk={() => setMessage(null)}
          />
        )}

        <div style={{ textAlign: "right" }}>
          <Button
            secondary
            className={cStyles.buttonSecond}
            onClick={() => onCloseDialog(null)}
          >
            <Ico name="cancel" style={{ marginRight: "10px" }} />
            Close
          </Button>
        </div>
      </Segment>
    </Portal>
  );
};

export default Applications;
